// Generic building painter for the 2x scene: roof (material + parapet + props) and the shared façade base (wall, edges,
// cornice, upper windows); the shopfront itself comes from the front painter registered for the building's kind.
// The Kabukicho art pass (hd-kabuki-*, hd-hachi-*, hd-kanemori-*, hd-neon-restoration) paints over the result by building id.
import type { HdBuilding } from '../../common/src/data';
import { hash2 } from '../../common/src/util';
import { HT, K, type G, px, speckle, disc, frame, dk, lt, alpha } from './hd-kit';
import { paintOkuboRoof, paintOkuboFront } from './hd-okubo-refresh';
import { paintNishiRoof, paintNishiFront } from './hd-nishi-refresh';
import { needsBackWall, BLIND_FRONT } from './hd-blind';
import { paintTwinBase } from './hd-twin-towers';
import { FRONTS, ROOFS, ROOF_PROPS, type LightFn } from './hd-registry';
import { cornice, windowPane, wallAc } from './hd-parts';
import './hd-fronts-index';
import { paintKabukiFinalPass } from './hd-kabuki-final-pass';
import { paintKanemoriLandmark } from './hd-kanemori-landmark';
import { paintKanemoriShops, KANEMORI_SHOPS } from './hd-kanemori-shops';
import { paintNeonRestoration } from './hd-neon-restoration';
import { paintKabukiLandmarks, LANDMARK_CUSTOM_FRONTS } from './hd-kabuki-landmark-study';
import { paintKabukiNorth, NORTH_STUDY_BUILDINGS } from './hd-kabuki-north-study';
import { paintKabukiCentral, CENTRAL_STUDY_BUILDINGS } from './hd-kabuki-central-study';
import { paintKabukiNortheast, NORTHEAST_STUDY_BUILDINGS } from './hd-kabuki-northeast-study';
import { paintKabukiEdgeStudy, EDGE_STUDY_BUILDINGS } from './hd-kabuki-edge-study';
import { paintKabukiRailStudy, RAIL_STUDY_BUILDINGS } from './hd-kabuki-rail-study';
import { paintKabukiEastStudy, EAST_STUDY_BUILDINGS } from './hd-kabuki-east-study';
import { paintHachiNeighborhood, NEIGHBORHOOD_BUILDINGS } from './hd-hachi-neighborhood';
import { paintHachiBuilding } from './hd-hachi-study';

const CUSTOM_FACADES = new Set<string>([...NEIGHBORHOOD_BUILDINGS, ...EAST_STUDY_BUILDINGS, ...RAIL_STUDY_BUILDINGS, ...EDGE_STUDY_BUILDINGS, ...NORTHEAST_STUDY_BUILDINGS, ...CENTRAL_STUDY_BUILDINGS, ...NORTH_STUDY_BUILDINGS, ...LANDMARK_CUSTOM_FRONTS, ...KANEMORI_SHOPS]);

/** a generator: yields between roof, roof props and façade so the renderer can spread a big building over several frames */
export function* paintBuilding(g: G, b: HdBuilding, x: number, y: number, L: LightFn): Generator<void> {
  if (paintTwinBase(g, b, x, y, L)) return; // the Kanemori Twin Towers draw their own shaft (hd-twin-towers.ts)
  const w = b.w * HT, h = b.h * HT, f = b.face * HT, rh = h - f;
  roof(g, b, x, y, w, rh, f > 0); yield;
  if (paintOkuboRoof(g, b, x, y)) yield;
  if (paintNishiRoof(g, b, x, y)) yield;
  for (const p of b.props ?? []) {
    const paint = ROOF_PROPS.get(p[0] as string);
    if (paint) paint({ g, x: x + Math.round((p[1] as number) * HT), y: y + Math.round((p[2] as number) * HT), bx: x, by: y, p, L }); yield;
  }
  if (f > 0 && needsBackWall(b)) { facade(g, b, x, y + rh, w, f, L, BLIND_FRONT); return; } // nothing to open onto: back wall only
  if (f > 0 && !CUSTOM_FACADES.has(b.id)) facade(g, b, x, y + rh, w, f, L);
  paintHachiBuilding(g, b, x, y, L);
  paintHachiNeighborhood(g, b, x, y, L);
  paintKabukiEastStudy(g, b, x, y, L);
  paintKabukiRailStudy(g, b, x, y, L);
  paintKabukiEdgeStudy(g, b, x, y, L);
  paintKabukiNortheast(g, b, x, y, L);
  paintKabukiCentral(g, b, x, y, L);
  paintKabukiNorth(g, b, x, y, L);
  paintKabukiLandmarks(g, b, x, y, L);
  paintKanemoriShops(g, b, x, y, L);
  paintKanemoriLandmark(g, b, x, y);
  paintKabukiFinalPass(g, b, x, y, L);
  paintNeonRestoration(g, b, x, y, L);
  paintOkuboFront(g, b, x, y, L);
  paintNishiFront(g, b, x, y, L);
}

function roof(g: G, b: HdBuilding, x: number, y: number, w: number, h: number, hasFace: boolean) {
  const seed = hash2(b.x, b.y) * 1000;
  const spec = ROOFS.get(b.roof);
  if (!spec) return;
  spec.paint(g, x, y, w, h, seed);
  if (spec.parapet === false) return;
  // parapet: dark outline, lit coping on the top/left, shaded on the bottom/right, and its shadow on the roof
  frame(g, x, y, w, h, K.ink, 2);
  px(g, x + 2, y + 2, w - 4, 4, K.concreteHi); px(g, x + 2, y + 2, 4, h - 4, K.concreteHi); px(g, x + 2, y + 2, w - 4, 1, lt(K.concreteHi, 0.2));
  px(g, x + w - 6, y + 6, 4, h - 8, K.concreteLo); if (!hasFace) px(g, x + 6, y + h - 6, w - 8, 4, K.concreteLo); else px(g, x + 6, y + h - 3, w - 8, 3, K.concreteHi);
  px(g, x + 6, y + 6, w - 12, 6, alpha(K.night, 0.38)); px(g, x + 6, y + 12, 5, h - 18, alpha(K.night, 0.32));
  // small clutter so big roofs aren't empty: drains and vent caps along the parapet
  for (let i = 40; i < w - 30; i += 70) if (hash2(seed, i) < 0.6) { px(g, x + i, y + 14, 6, 4, K.ink); px(g, x + i + 1, y + 15, 4, 2, K.metalLo); }
  for (let j = 50; j < h - 30; j += 60) if (hash2(j, seed) < 0.5) { disc(g, x + 18, y + j, 3, 3, K.ink); disc(g, x + 18, y + j - 1, 2, 2, K.metalHi); }
}

function facade(g: G, b: HdBuilding, x: number, y: number, w: number, h: number, L: LightFn, kind = b.kind) {
  const spec = FRONTS.get(kind); if (!spec) return;
  const gf = Math.min(h - 10, 44), gy = y + h - gf; // ground floor
  const wall = spec.wall ?? K.wall;
  px(g, x, y, w, h, wall);
  speckle(g, x, y, w, h, dk(wall, 0.25), 0.04, 31);
  for (let j = y + 12; j < gy; j += 8) px(g, x, j, w, 1, alpha(K.night, 0.12)); // panel courses
  px(g, x, y, 2, h, K.ink); px(g, x + w - 2, y, 2, h, K.ink); px(g, x + 2, y, 2, h, lt(wall, 0.12)); px(g, x + w - 5, y, 3, h, dk(wall, 0.3)); // building edges
  cornice(g, x, y, w);
  px(g, x, y + h - 4, w, 3, dk(wall, 0.45)); px(g, x, y + h - 1, w, 1, K.ink); // kickplate
  const upper = gy - (y + 10); // rows of wall above the shopfront
  const seed = hash2(b.x * 3, b.y * 7);
  const mode = spec.upper ?? 'windows';
  if (mode === 'curtain') {
    for (let fy = y + 12; fy + 26 <= gy - 4; fy += 30) {
      px(g, x + 4, fy + 24, w - 8, 3, K.concreteLo);
      for (let i = x + 8; i + 12 <= x + w - 8; i += 16) windowPane(g, i, fy + 2, 12, 20, hash2(i, fy + seed), L);
    }
  } else if (mode === 'windows' && upper >= 26) {
    for (let i = x + 10; i + 16 <= x + w - 8; i += 26) windowPane(g, i, y + 14, 16, Math.min(18, upper - 8), hash2(i + seed, y), L);
    if (spec.wallAc) for (let i = x + 30; i < x + w - 20; i += 52) wallAc(g, i, y + 18);
  }
  spec.paint({ g, b, x, y, w, h, gf, gy, upper, seed, wall, sign: kind === b.kind ? b.sign : undefined, L });
}
